import { AccessTime } from "@mui/icons-material"
import WarningIcon from "@mui/icons-material/Warning"
import AppBar from "@mui/material/AppBar"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import { Project } from "graphql/types"
import { useStoreState } from "hooks/storeHooks"
import { useUserProfile } from "hooks/useUserProfile"
import { JSX, useEffect, useState } from "react"
import { useParams } from "react-router"

import { allExaminations } from "utils/allExaminations"
import { formatTime } from "utils/dateTimeUtils"
import { getExaminationWorkingTime } from "utils/getExaminationWorkingTime"
import { getTotalEstimatedTime } from "../../utils/getTotalEstimatedTime"

const Header = (): JSX.Element => {
  const { projectID, examinationID } = useParams()
  const [project, setProject] = useState<Project>()
  const [currentTime, setCurrentTime] = useState(Date.now())

  const projectsState = useStoreState((state) => state.projects)
  const { profilePhotoUrl, displayName, initials, isAuthenticated } = useUserProfile()

  useEffect(() => {
    const newProject = projectsState.find((project) => project && project?.id === projectID)
    if (newProject) {
      setProject(newProject)
    }
  }, [projectID, projectsState])

  // Real-time clock for active timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Helper function to calculate real-time working time including active timers
  const getRealTimeWorkingTime = () => {
    if (!project || !examinationID) return 0

    const examination = allExaminations(project).find((item) => item.id === examinationID)
    if (!examination) return 0

    // Start with the saved working time
    let totalTime = getExaminationWorkingTime(examination)

    // Add current session time for any running timers
    // We need to access the running timer data from TaskPage somehow
    // For now, we'll check if there's timer data in localStorage or sessionStorage
    try {
      const timerData = sessionStorage.getItem("activeTimer")
      if (timerData) {
        const { taskId, startTime } = JSON.parse(timerData)
        if (startTime) {
          const sessionTime = Math.floor((currentTime - new Date(startTime).getTime()) / 1000)
          totalTime += sessionTime
        }
      }
    } catch (e) {
      // Ignore errors accessing sessionStorage
    }

    return totalTime
  }

  return (
    <>
      <AppBar position="static">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              noWrap
              component="a"
              sx={{
                flexGrow: 1,
              }}
            >
              {project?.lead.businessAccount}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {project && examinationID && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTime />
                  <Typography>
                    {formatTime(getRealTimeWorkingTime())}/
                    {formatTime(getTotalEstimatedTime(allExaminations(project), examinationID))}
                  </Typography>
                  {getRealTimeWorkingTime() >
                    getTotalEstimatedTime(allExaminations(project), examinationID) && (
                    <WarningIcon sx={{ fontSize: 16 }} color="warning" />
                  )}
                </Box>
              )}

              {/* User info and avatar */}
              {isAuthenticated && (
                <>
                  <Typography variant="body2" sx={{ color: "inherit" }}>
                    {displayName}
                  </Typography>
                  <Avatar
                    src={profilePhotoUrl || undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: profilePhotoUrl ? "transparent" : "secondary.main",
                      fontSize: "0.875rem",
                    }}
                  >
                    {!profilePhotoUrl && initials}
                  </Avatar>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  )
}

export default Header
